import React, { useState, useEffect } from 'react';
import { useWallet } from '../hooks/useWallet';
import './Faucet.css';

const API = 'https://api.amitis.network';

export default function Faucet() {
  const { wallet } = useWallet();
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [customAddress, setCustomAddress] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/status`);
      const data = await res.json();
      setStatus(data);
    } catch (e) {}
  };

  const claim = async () => {
    const address = wallet?.address || customAddress.trim();
    if (!address) return;

    setClaiming(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch(`${API}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setResult(data);
        fetchStatus();
      } else {
        setError(data.error || 'Claim failed');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const address = wallet?.address || customAddress;
  const canClaim = address.startsWith('amitis1') && address.length > 20;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-pill mono">Faucet</div>
        <h1 className="page-title">
          Get free <span className="italic green">AMTS.</span>
        </h1>
        <p className="page-sub">
          Claim free AMTS to start using Amitis Network. Experience the 80% fee rebate — pay a fee, get 80% back instantly.
        </p>
      </div>

      {/* Faucet Stats */}
      {status && (
        <div className="faucet-stats">
          <div className="faucet-stat">
            <span className="mono">Available</span>
            <strong>{status.balance_amts?.toLocaleString(undefined, { maximumFractionDigits: 0 })} AMTS</strong>
          </div>
          <div className="faucet-stat">
            <span className="mono">Per Claim</span>
            <strong className="green">{status.gift_amount_amts} AMTS</strong>
          </div>
          <div className="faucet-stat">
            <span className="mono">Total Claims</span>
            <strong>{status.total_claims?.toLocaleString()}</strong>
          </div>
          <div className="faucet-stat">
            <span className="mono">Cooldown</span>
            <strong>{status.cooldown_hours}h</strong>
          </div>
        </div>
      )}

      {/* Claim Card */}
      <div className="claim-card">
        <div className="claim-amount">
          <span className="claim-number green">{status ? status.gift_amount_amts : '—'}</span>
          <div className="claim-amount-info">
            <strong>AMTS</strong>
            <span className="mono muted">free tokens</span>
          </div>
        </div>

        {wallet ? (
          <div className="claim-address">
            <span className="mono muted">Sending to:</span>
            <span className="mono">{wallet.address}</span>
          </div>
        ) : (
          <div className="claim-input-wrap">
            <label className="mono muted">Enter your Amitis address</label>
            <input
              className="claim-input"
              type="text"
              placeholder="amitis1..."
              value={customAddress}
              onChange={e => setCustomAddress(e.target.value)}
            />
          </div>
        )}

        <button
          className="claim-btn"
          onClick={claim}
          disabled={claiming || !canClaim}
        >
          {claiming ? (
            <><span className="spinner-green" /> Claiming...</>
          ) : (
            `Claim ${status ? status.gift_amount_amts : '—'} AMTS`
          )}
        </button>

        {error && (
          <div className="claim-error">
            <span>⚠️</span> {error}
          </div>
        )}

        {result && (
          <div className="claim-success">
            <div className="success-icon">✓</div>
            <div className="success-content">
              <strong>{status ? status.gift_amount_amts : ''} AMTS sent!</strong>
              <span className="mono">tx: {result.txhash?.slice(0, 20)}...</span>
              <p className="success-note">{result.message}</p>
            </div>
          </div>
        )}
      </div>

      {/* Rebate Demo */}
      <div className="rebate-demo">
        <h3>What happens when you send AMTS?</h3>
        <div className="rebate-steps">
          <div className="rebate-step">
            <div className="step-num">1</div>
            <div className="step-content">
              <strong>You send a transaction</strong>
              <span>0.3% fee is charged (e.g. 300 uamts on a 100,000 uamts transfer)</span>
            </div>
          </div>
          <div className="rebate-arrow">↓</div>
          <div className="rebate-step highlight">
            <div className="step-num green">2</div>
            <div className="step-content">
              <strong className="green">80% returned to you instantly</strong>
              <span>240 uamts comes back to your wallet automatically</span>
            </div>
          </div>
          <div className="rebate-arrow">↓</div>
          <div className="rebate-step">
            <div className="step-num">3</div>
            <div className="step-content">
              <strong>You paid 60 uamts effective</strong>
              <span>0.06% effective fee — 5× cheaper than most chains</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
